import { useEffect, useState } from "react";
import useBookmark from "./useBookmark";
import useProperties from "./useProperties";
import useDialog from "./useDialog";
import useAuth from "./useAuth";

export const usePropertyActions = (property) => {
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isDoubtProperty, setIsDoubtProperty] = useState(false);
  const [isDiscardedProperty, setIsDiscardedProperty] = useState(false);
  const [removeWarningPopupOpen, setRemoveWarningPopupOpen] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [conflictingActionType, setConflictingActionType] = useState(null);
  const [isBookmarkPropertyHasNote, setIsBookmarkPropertyHasNote] =
    useState(null);
  const [chooseOrganiseList, setChooseOrganiseList] = useState(false);

  const {
    removeSavedBookmark,
    bookmarkedPropertyIds,
    updateBookmarkNote,
    bookmarkProperty,
  } = useBookmark();
  const {
    addDoubtProperty,
    removeDoubtProperty,
    addDiscardedProperty,
    removeDiscardedProperty,
    doubtPropertyIds,
    discardedPropertyIds,
  } = useProperties();

  const { openSigninDialog } = useDialog();
  const { user } = useAuth();

  useEffect(() => {
    // Check if the current property is bookmarked, doubted, or discarded
    const isPropertyBookmarked =
      bookmarkedPropertyIds.length > 0 &&
      bookmarkedPropertyIds?.some(
        (bookmark) => bookmark.propertyId === property._id
      );

    const isBookmarkPropertyHasNote =
      (bookmarkedPropertyIds.length > 0 &&
        bookmarkedPropertyIds.find(
          (bookmark) =>
            bookmark.propertyId === property._id &&
            bookmark.note &&
            bookmark.note.trim() !== ""
        )?.note) ||
      null;

    const isPropertyDoubted =
      doubtPropertyIds.length > 0 &&
      doubtPropertyIds?.some((id) => id === property._id);

    const isPropertyDiscarded =
      discardedPropertyIds.length > 0 &&
      discardedPropertyIds?.some((id) => id === property._id);

    // Update state based on the checks
    setIsBookmarked(isPropertyBookmarked);
    setIsBookmarkPropertyHasNote(isBookmarkPropertyHasNote);
    setIsDoubtProperty(isPropertyDoubted);
    setIsDiscardedProperty(isPropertyDiscarded);
  }, [
    bookmarkedPropertyIds,
    doubtPropertyIds,
    discardedPropertyIds,
    property._id,
  ]);

  const getRemoveMessage = (type) => {
    switch (type) {
      case "bookmark":
        return "Vuoi rimuovere questa proprietà dall’organizer?";
      case "doubt":
        return "Vuoi rimuovere questa proprietà dalla lista dubbi?";
      case "discard":
        return "Vuoi rimuovere questa proprietà dalla lista delle proprietà scartate?";
      default:
        return "";
    }
  };

  // Handle add/remove actions (bookmark, doubt, discard)
  const handleAction = async (type) => {
    if (!user) {
      openSigninDialog();
      return;
    }

    setActionType(type);
    const conflictMessages = {
      bookmark: {
        doubt:
          "Per aggiungere la proprietà nell'organizer bisogna rimuoverla dalla lista dubbi, vuoi rimuoverla?",
        discard:
          "Per aggiungere la proprietà nell'organizer bisogna rimuoverla dalla lista delle proprietà scartate, vuoi rimuoverla?",
      },
      doubt: {
        bookmark:
          "Per aggiungere la proprietà nella lista dubbi bisogna rimuoverla dall'organizer, vuoi rimuoverla?",
        discard:
          "Per aggiungere la proprietà nella lista dubbi bisogna rimuoverla dalla lista delle proprietà scartate, vuoi rimuoverla?",
      },
      discard: {
        bookmark:
          "Se vuoi scartare la proprietà devi rimuoverla dalla lista dubbi, vuoi rimuoverla?",
        doubt:
          "Se vuoi scartare la proprietà bisogna rimuoverla dalla lista dubbi, vuoi rimuoverla?",
      },
    };

    if (type === "bookmark") {
      if (isBookmarked) {
        setRemoveWarningPopupOpen(getRemoveMessage("bookmark"));
        setConflictingActionType(null);
      } else if (isDoubtProperty) {
        setRemoveWarningPopupOpen(conflictMessages.bookmark.doubt);
        setConflictingActionType("doubt");
      } else if (isDiscardedProperty) {
        setRemoveWarningPopupOpen(conflictMessages.bookmark.discard);
        setConflictingActionType("discard");
      } else {
        setChooseOrganiseList(true);
      }
    } else if (type === "doubt") {
      if (isDoubtProperty) {
        setRemoveWarningPopupOpen(getRemoveMessage("doubt"));
        setConflictingActionType(null);
      } else if (isBookmarked) {
        setRemoveWarningPopupOpen(conflictMessages.doubt.bookmark);
        setConflictingActionType("bookmark");
      } else if (isDiscardedProperty) {
        setRemoveWarningPopupOpen(conflictMessages.doubt.discard);
        setConflictingActionType("discard");
      } else {
        setIsDoubtProperty(true);
        await addDoubtProperty(property._id, property?.average_gross_yield);
      }
    } else if (type === "discard") {
      if (isDiscardedProperty) {
        setRemoveWarningPopupOpen(getRemoveMessage("discard"));
        setConflictingActionType(null);
      } else if (isBookmarked) {
        setRemoveWarningPopupOpen(conflictMessages.discard.bookmark);
        setConflictingActionType("bookmark");
      } else if (isDoubtProperty) {
        setRemoveWarningPopupOpen(conflictMessages.discard.doubt);
        setConflictingActionType("doubt");
      } else {
        setIsDiscardedProperty(true);
        await addDiscardedProperty(property._id, property?.average_gross_yield);
      }
    }
  };

  // Handle remove and add property when click warning popup confirm button
  const handleRemoveConfirm = async () => {
    try {
      if (actionType === "bookmark") {
        if (conflictingActionType === "doubt") {
          // remove doubt
          setIsDoubtProperty(false);
          await removeDoubtProperty(property._id);
          // add bookmark
          setChooseOrganiseList(true);
        } else if (conflictingActionType === "discard") {
          // remove discard
          setIsDiscardedProperty(false);
          await removeDiscardedProperty(property._id);
          // add bookmark
          setChooseOrganiseList(true);
        } else {
          setIsBookmarked(false);
          await removeSavedBookmark(property._id);
        }
      } else if (actionType === "doubt") {
        if (conflictingActionType === "bookmark") {
          // remove bookmark
          setIsBookmarked(false);
          await removeSavedBookmark(property._id);

          // add doubt
          setIsDoubtProperty(true);
          await addDoubtProperty(property._id, property?.average_gross_yield);
        } else if (conflictingActionType === "discard") {
          // remove discard
          setIsDiscardedProperty(false);
          await removeDiscardedProperty(property._id);

          // add doubt
          setIsDoubtProperty(true);
          await addDoubtProperty(property._id, property?.average_gross_yield);
        } else {
          setIsDoubtProperty(false);
          await removeDoubtProperty(property._id);
        }
      } else if (actionType === "discard") {
        if (conflictingActionType === "bookmark") {
          // remove bookmark
          setIsBookmarked(false);
          await removeSavedBookmark(property._id);

          // add discard
          setIsDiscardedProperty(true);
          await addDiscardedProperty(
            property._id,
            property?.average_gross_yield
          );
        } else if (conflictingActionType === "doubt") {
          // remove doubt
          setIsDoubtProperty(false);
          await removeDoubtProperty(property._id);

          // add discard
          setIsDiscardedProperty(true);
          await addDiscardedProperty(
            property._id,
            property?.average_gross_yield
          );
        } else {
          setIsDiscardedProperty(false);
          await removeDiscardedProperty(property._id);
        }
      }
    } catch (error) {
      // revert orginal form if error
      if (actionType === "bookmark") {
        if (conflictingActionType === "doubt") {
          setIsDoubtProperty(true);
          setIsBookmarked(false);
        } else if (conflictingActionType === "discard") {
          setIsDiscardedProperty(true);
          setIsBookmarked(false);
        } else {
          setIsBookmarked(true);
        }
      } else if (actionType === "doubt") {
        if (conflictingActionType === "bookmark") {
          setIsBookmarked(true);
          setIsDoubtProperty(false);
        } else if (conflictingActionType === "discard") {
          setIsDiscardedProperty(true);
          setIsDoubtProperty(false);
        } else {
          setIsDoubtProperty(true);
        }
      } else if (actionType === "discard") {
        if (conflictingActionType === "bookmark") {
          setIsBookmarked(true);
          setIsDiscardedProperty(false);
        } else if (conflictingActionType === "doubt") {
          setIsDoubtProperty(true);
          setIsDiscardedProperty(false);
        } else {
          setIsDiscardedProperty(true);
        }
      }
      console.error(`Error removing ${actionType}:`, error);
    } finally {
      setRemoveWarningPopupOpen(null);
      setActionType(null);
      setConflictingActionType(null);
    }
  };

  // Update bookmark note
  const handleUpdateNote = async (note) => {
    try {
      await updateBookmarkNote(property?._id, note);
      setIsBookmarkPropertyHasNote(true);
    } catch (error) {}
  };

  // Handle add  bookmark
  const handleAddBookmark = async () => {
    setIsBookmarked(true);
    try {
      await bookmarkProperty(property?._id, property?.average_gross_yield);
    } catch (error) {
      setIsBookmarked(false);
    }
  };

  return {
    isBookmarked,
    setIsBookmarked,
    isDoubtProperty,
    isDiscardedProperty,
    setIsDiscardedProperty,
    handleAction,
    handleRemoveConfirm,
    removeWarningPopupOpen,
    setRemoveWarningPopupOpen,
    isBookmarkPropertyHasNote,
    chooseOrganiseList,
    setChooseOrganiseList,
    handleUpdateNote,
    handleAddBookmark,
  };
};
